import React from "react";
import { ShieldCheck, FileCheck, Users, ClipboardList } from "lucide-react";
import CTABand from "../components/CTABand";
import { MEDIA } from "../lib/constants";
import { useSEO } from "../lib/seo";

export default function SurveyReadiness() {
  useSEO({
    title:
      "Nursing Home Survey Readiness Consulting · St. Louis, MO",
    description:
      "Mock surveys, documentation audits, IDT readiness drills, and survey-week coaching for Missouri skilled nursing facilities. Walk into your next annual or complaint survey prepared, not panicked.",
    path: "/survey-readiness",
  });
  return (
    <div data-testid="survey-page" className="bg-ivory">
      {/* HERO */}
      <section className="section-pad pt-32 bg-paper relative overflow-hidden">
        <div className="container-luxe grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="overline mb-5">Practice Area · Survey Readiness and Compliance</div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-navy leading-[0.95] mb-6">
              Walk in <span className="italic text-teal">prepared.</span><br />
              Walk out clean.
            </h1>
            <p className="font-sans text-lg text-navy/75 max-w-2xl leading-relaxed">
              F-tags are symptoms. The operational dysfunction underneath is
              what surveyors are really measuring, and it is what gets a
              facility cited a second time. We surface the pattern and rebuild
              the system before the next survey window opens.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-square relative overflow-hidden">
              <img
                src={MEDIA.hospitalHallway}
                alt="Survey readiness"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 border border-gold" />
            </div>
          </div>
        </div>
      </section>

      {/* DELIVERABLES */}
      <section className="section-pad" data-testid="deliverables">
        <div className="container-luxe">
          <div className="overline mb-5">Engagement Deliverables</div>
          <h2 className="font-serif text-4xl sm:text-5xl text-navy leading-tight mb-16 max-w-3xl">
            Five disciplines that separate survey-ready facilities from the rest.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-champagne-200 border border-champagne-200">
            {[
              {
                icon: ShieldCheck,
                title: "Mock Survey",
                body: "Conducted with the same rigor as a state entry. Includes documentation review, observation, interviews, and an exit conference.",
              },
              {
                icon: FileCheck,
                title: "Documentation Audit",
                body: "Sample based audit against the State Operations Manual, not against internal expectations.",
              },
              {
                icon: Users,
                title: "IDT Readiness Drill",
                body: "Tabletop scenarios that surface communication breakdowns before surveyors do.",
              },
              {
                icon: ClipboardList,
                title: "Plan of Correction Engineering",
                body: "Plans of correction the facility can actually sustain past day 90. Each item has measurement, an owner, and an audit cadence.",
              },
              {
                icon: ShieldCheck,
                title: "QAPI Integration",
                body: "Survey findings linked directly into the QAPI committee so corrective work compounds rather than dissipates.",
              },
              {
                icon: FileCheck,
                title: "Survey Week Coaching",
                body: "On call advisory throughout the actual survey window. Entrance conference, ongoing observation, and exit conference.",
              },
            ].map((d) => (
              <div
                key={d.title}
                className="bg-ivory p-8 hover:bg-champagne-100 transition-colors group"
              >
                <div className="w-12 h-12 border border-gold flex items-center justify-center mb-5 group-hover:bg-gold transition-colors">
                  <d.icon className="h-5 w-5 text-gold group-hover:text-navy" />
                </div>
                <h3 className="font-serif text-xl text-navy mb-3">{d.title}</h3>
                <p className="font-sans text-sm text-navy/70 leading-relaxed">
                  {d.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABand
        testId="survey-cta-band"
        eyebrow="Before the Window Opens"
        headline="The best survey is the one that did not surprise anyone."
        sub="Bring AthenaCrest in 60 to 120 days ahead of the survey window for the cleanest possible outcome."
      />
    </div>
  );
}
